import { useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import CustomerPopup from "../components/CustomerPopup";
import AddProductPopup from "../components/AddProductPopup";
import { offlineDB } from "../offline/db";
import React, { useEffect } from "react";
import { useBarcodeScanner } from "../hooks/useBarcodeScanner";

import {
  reduceOfflineStock,
  saveOfflineInvoice,
} from "../offline/billingHelpers";

import { useAuth } from "../context/AuthContext";

const Billing = () => {
  const navigate = useNavigate();
  const { user, branch } = useAuth();
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [cart, setCart] = useState([]);
  const [paymentMode, setPaymentMode] = useState("CASH");
  const [showCustomerPopup, setShowCustomerPopup] = useState(false);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [newProductName, setNewProductName] = useState("");
  const [billType, setBillType] = useState("NONGST");
  const [discountType, setDiscountType] = useState("NONE"); // NONE | PERCENT | AMOUNT
  const [discountValue, setDiscountValue] = useState(0);
  const discountRef = useRef(null);
  const justAddedRef = useRef(false);
  const discountTypeRef = useRef(null);

    let discountAmount = 0;

  useBarcodeScanner(async (code) => {
    console.log("📦 Barcode scanned:", code);
    if (showCustomerPopup || showAddProduct) return;

    try {
      // ONLINE
      if (navigator.onLine) {
        const res = await api.get(`/products/search?q=${code}`);
        if (res.data.length === 1) {
          addToCart(res.data[0]);
        } else {
          alert("Product not found");
        }
        return;
      }

      // OFFLINE
      const product = await offlineDB.products
        .where("barcode")
        .equals(code)
        .first();

      if (product) {
        addToCart(product);
      } else {
        alert("Product not found (offline)");
      }
    } catch (err) {
      console.error("SCAN ERROR:", err);
    }
  });

  useEffect(() => {
    if (!user?.branch_id) {
      console.error("❌ User has no branch assigned", user);
    }
  }, [user]);

  useEffect(() => {
    const warnOnReload = (e) => {
      if (!navigator.onLine && cart.length > 0) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", warnOnReload);
    return () => window.removeEventListener("beforeunload", warnOnReload);
  }, [cart]);

const moveToNextField = (e) => {
  if (e.key !== "Enter") return;

  e.preventDefault();

  const form = e.target.form || document;
  const focusable = Array.from(
    form.querySelectorAll(
      'input, select, textarea, button'
    )
  ).filter(
    el => !el.disabled && el.type !== "hidden"
  );

  const index = focusable.indexOf(e.target);
  if (index > -1 && index < focusable.length - 1) {
    focusable[index + 1].focus();
  }
};

  /* ================= GST CALC ================= */
  const calculate = useCallback(
    (price, qty, gst) => {
      const total = price * qty;

      // NORMAL BILL → NO GST
      if (billType === "NORMAL") {
        return {
          total: +total.toFixed(2),
          taxable: +total.toFixed(2),
          cgst: 0,
          sgst: 0,
        };
      }

      // GST BILL
      const taxable = gst > 0 ? (total * 100) / (100 + gst) : total;
      const gstAmt = total - taxable;

      return {
        total: +total.toFixed(2),
        taxable: +taxable.toFixed(2),
        cgst: +(gstAmt / 2).toFixed(2),
        sgst: +(gstAmt / 2).toFixed(2),
      };
    },
    [billType],
  );

  useEffect(() => {
    setCart((prev) =>
      prev.map((i) => ({
        ...i,
        ...calculate(i.price, i.quantity, i.gst_percent),
      })),
    );
  }, [billType, calculate]);

  const changeQty = (id, qty) => {
    if (qty <= 0) {
      setCart((prev) => prev.filter((i) => i.product_id !== id));
      return;
    }

    setCart((prev) =>
      prev.map((i) =>
        i.product_id === id
          ? {
              ...i,
              quantity: qty,
              ...calculate(i.price, qty, i.gst_percent),
            }
          : i,
      ),
    );
  };

  const totals = cart.reduce(
    (acc, i) => {
      acc.taxable += i.taxable;
      acc.cgst += i.cgst;
      acc.sgst += i.sgst;
      acc.grand += i.total;
      return acc;
    },
    { taxable: 0, cgst: 0, sgst: 0, grand: 0 },
  );

  const subtotal = totals.grand;

  /* ================= SEARCH ================= */

const searchProducts = async (value) => {
  setSearch(value);
  setSelectedIndex(-1);

  const isBarcode = /^\d+$/.test(value);

  if (!isBarcode && value.length < 2) {
    setSuggestions([]);
    return;
  }

  // ONLINE
  if (navigator.onLine) {
    const res = await api.get(`/products/search?q=${value}`);
    const results = res.data || [];

    const exact = results.find(p => p.barcode === value);

    if (exact) {
      addToCart(exact);
      return;
    }

    setSuggestions(results);

    if (results.length === 0) {
      setNewProductName(value);
    }

    return;
  }

  // OFFLINE
  const local = await offlineDB.products
    .filter(p =>
      p.barcode === value ||
      p.name?.toLowerCase().includes(value.toLowerCase())
    )
    .limit(10)
    .toArray();

  const exactLocal = local.find(p => p.barcode === value);

  if (exactLocal) {
    addToCart(exactLocal);
    return;
  }

  setSuggestions(local);
};


  const checkOfflineStock = async () => {
    const currentBranchId = user?.branch_id;

    if (!currentBranchId) {
      throw new Error("Branch not ready. Please wait.");
    }

    const inventory = await offlineDB.inventory.toArray();

    const stockMap = {};

    inventory.forEach((i) => {
      if (i.branch_id === currentBranchId) {
        stockMap[i.product_id] = i.quantity;
      }
    });

    for (const item of cart) {
      const available = stockMap[item.product_id] ?? 0;

      if (available < item.quantity) {
        throw new Error(
          `Insufficient stock for ${item.name} (Available: ${available})`,
        );
      }
    }
  };

  /* ================= ADD TO CART ================= */
  const addToCart = (product) => {
    setCart((prev) => {
      const existing = prev.find((p) => p.product_id === product.product_id);

      if (existing) {
        return prev.map((p) =>
          p.product_id === product.product_id
            ? {
                ...p,
                quantity: p.quantity + 1,
                ...calculate(p.price, p.quantity + 1, p.gst_percent),
              }
            : p,
        );
      }

      return [
        ...prev,
        {
          ...product,
          quantity: 1,
          ...calculate(product.price, 1, product.gst_percent),
        },
      ];
    });

    setSearch("");
    setSuggestions([]);
  };

  /* ================= STOCK CHECK ================= */

  const checkStockBeforeCustomer = async () => {
    if (cart.length === 0) {
      alert("Cart empty");
      return;
    }

    // OFFLINE MODE
    // OFFLINE MODE
    if (!navigator.onLine) {
      try {
        await checkOfflineStock();
        setShowCustomerPopup(true);
      } catch (err) {
        alert(err.message);
      }
      return;
    }

    // ONLINE MODE (existing API)
    try {
      await api.post("/pos/invoice/check-stock", {
        items: cart.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
        })),
      });

      setShowCustomerPopup(true);
    } catch (err) {
      alert("Stock check failed");
    }
  };

  if (discountType === "PERCENT") {
  discountAmount = (subtotal * discountValue) / 100;
}

if (discountType === "AMOUNT") {
  discountAmount = discountValue;
}

// Safety
if (discountAmount > subtotal) {
  discountAmount = subtotal;
}

const payable = subtotal - discountAmount;

  /* ================= FINAL CONFIRM ================= */
  const confirmInvoice = async (customer) => {
  const branchId = user?.branch_id;
  if (!branchId) {
    alert("Branch not ready. Please retry.");
    return;
  }

  setShowCustomerPopup(false);

  const payload = {
    items: cart.map((i) => ({
      product_id: i.product_id,
      product_name: i.name,      // 🔥 REQUIRED FOR OFFLINE PRINT
      quantity: i.quantity,
      price: i.price,
      total: i.total,
    })),

    bill_type: billType,
    subtotal,
    discount_type: discountType,
    discount_value: discountValue,
    discount_amount: discountAmount,
    grand_total: payable,

    payment_mode: paymentMode,
    customer_mobile: customer.customer_mobile,
    customer_name: customer.customer_name,
    created_by_name: user.full_name,
  };

  try {
    if (navigator.onLine) {
      const res = await api.post("/pos/invoice", payload);
      setCart([]);
      navigate(`/print/${res.data.invoice_id}`);
    } else {
      // 🔥 OFFLINE FLOW
      await reduceOfflineStock(branchId, cart);

      const offlineInvoiceId = await saveOfflineInvoice(
        payload,
        branch // full branch object
      );

      setCart([]);
      navigate(`/print/offline/${offlineInvoiceId}`);
    }
  } catch (err) {
    console.error("INVOICE ERROR:", err);
    alert("Invoice failed");
  }
};


  /* ================= UI (UNCHANGED) ================= */
  return (
    <div>
      <h2>Billing</h2>

      {/* ===== SEARCH INPUT ===== */}
      <div style={{ position: "relative" }}>
        <div className="form-row">
        <input
  type="text"
  placeholder="Scan / Enter barcode or product name"
  value={search}
  autoFocus
  onChange={(e) => {
    justAddedRef.current = false;
    searchProducts(e.target.value);
  }}
  onKeyDown={(e) => {
    // 🔽 Suggestion navigation
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex(prev =>
        prev < suggestions.length - 1 ? prev + 1 : 0
      );
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex(prev =>
        prev > 0 ? prev - 1 : suggestions.length - 1
      );
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();

      // 🔁 SECOND ENTER → jump to DISCOUNT
      if (justAddedRef.current) {
        justAddedRef.current = false;

        if (discountType === "NONE") {
          discountTypeRef.current?.focus();
        } else {
          discountRef.current?.focus();
        }

        return;
      }

      // 🔥 FIRST ENTER → add product
      let added = false;

      if (selectedIndex >= 0 && suggestions[selectedIndex]) {
        addToCart(suggestions[selectedIndex]);
        added = true;
      } else if (suggestions.length === 1) {
        addToCart(suggestions[0]);
        added = true;
      }

      if (added) {
        justAddedRef.current = true;
      }
    }
  }}
/>


        </div>

        {search.length >= 2 && (
          <ul className="search-dropdown">
            {suggestions.map((p, idx) => (
              <li
                key={p.product_id}
                onClick={() => addToCart(p)}
                className={idx === selectedIndex ? "active" : ""}
              >
                <div style={{ fontWeight: "bold" }}>{p.name}</div>
                <div style={{ fontSize: 12, color: "#555" }}>
                  Barcode: {p.barcode} | ₹{p.price}
                </div>
              </li>
            ))}

            {suggestions.length === 0 && (
              <li className="add-new" onClick={() => setShowAddProduct(true)}>
                ➕ Add “{newProductName}”
              </li>
            )}
          </ul>
        )}
      </div>

      {/* ===== CART ===== */}
      <table className="invoice-table print-container">
        <thead>
          <tr>
            <th>Item</th>
            <th>Qty</th>
            <th>Price</th>
            <th>Total</th>
            <th>X</th>
          </tr>
        </thead>
        <tbody>
          {cart.map((i) => (
            <tr key={i.product_id}>
              <td>{i.name}</td>
              <td>
                <button
                  class="qty-btn"
                  onClick={() => changeQty(i.product_id, i.quantity - 1)}
                >
                  -
                </button>
                {i.quantity}
                <button
                  class="qty-btn"
                  onClick={() => changeQty(i.product_id, i.quantity + 1)}
                >
                  +
                </button>
              </td>
              <td>{i.price}</td>
              <td>{i.total}</td>
              <td>
                <button
                  class="remove-btn"
                  onClick={() => changeQty(i.product_id, 0)}
                >
                  X
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ===== TOTALS ===== */}

      <div className="totals">
        <div>
          <span>Taxable</span>
          <span>₹{totals.taxable.toFixed(2)}</span>
        </div>
        <div>
          <span>CGST</span>
          <span>₹{totals.cgst.toFixed(2)}</span>
        </div>
        <div>
          <span>SGST</span>
          <span>₹{totals.sgst.toFixed(2)}</span>
        </div>
        <div className="grand">
          <span>Total</span>
          <span>₹{totals.grand.toFixed(2)}</span>
        </div>
        {discountType !== "NONE" && (
  <div className="totals">
    <div>
      <span>Discount</span>
      <span>-₹{discountAmount.toFixed(2)}</span>
    </div>
  </div>
)}
        <div className="grand">
  <span>Payable</span>
  <span>₹{payable.toFixed(2)}</span>
</div>
      </div>

      <br />

      <div className="discount-box form-row">
  <select
  ref={discountTypeRef}
    value={discountType}
    onChange={e => {
      setDiscountType(e.target.value);
      setDiscountValue(0);
    }}
    onKeyDown={moveToNextField}
  >
    <option value="NONE">No Discount</option>
    <option value="PERCENT">Discount %</option>
    <option value="AMOUNT">Discount ₹</option>
  </select>

  {discountType !== "NONE" && (
    <input
    ref={discountRef}
      type="number"
      placeholder={
        discountType === "PERCENT" ? "Discount %" : "Discount Amount"
      }
      value={discountValue}
      onChange={e => setDiscountValue(Number(e.target.value))}
      onKeyDown={moveToNextField}
    />
  )}
</div>

<br/>

      <div style={{ marginBottom: 10 }}>
        <label style={{ marginRight: 15 }}>
          <input
            type="radio"
            name="billType"
            value="NONGST"
            checked={billType === "NONGST"}
            onChange={() => setBillType("NONGST")}
            onKeyDown={moveToNextField}
          />{" "}
          Normal Bill
        </label>

        <label>
          <input
            type="radio"
            name="billType"
            value="GST"
            checked={billType === "GST"}
            onChange={() => setBillType("GST")}
            onKeyDown={moveToNextField}
          />{" "}
          GST Bill
        </label>
      </div>

      <br />

      <select
        value={paymentMode}
        onChange={(e) => setPaymentMode(e.target.value)}
        onKeyDown={moveToNextField}
      >
        <option value="CASH">Cash</option>
        <option value="UPI">UPI</option>
        <option value="CARD">Card</option>
      </select>

      <br />
      <br />

      <button className="finalize-btn" onClick={checkStockBeforeCustomer}>
        Finalize & Print
      </button>

      {showCustomerPopup && (
        <CustomerPopup
          onClose={() => setShowCustomerPopup(false)}
          onConfirm={confirmInvoice}
          autoFocusConfirm={true}
        />
      )}

      {showAddProduct && (
        <AddProductPopup
          initialName={newProductName}
          onClose={() => setShowAddProduct(false)}
          onAdded={(product) => {
            setShowAddProduct(false);
            addToCart(product);
          }}
        />
      )}
    </div>
  );
};

export default Billing;
